<template lang="pug">
.plan(:class="{ active }")
  .plan__bg-deco(aria-hidden="true")
    .deco-circle.deco-1
    .deco-circle.deco-2

  .plan__header
    svg.plan__wifi-icon(xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true")
      path(stroke-linecap="round" stroke-linejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z")
    h1.plan__name {{ nombre_comercial }}
    img.plan__logo(:src="require('@/assets/logo.png')" alt="GigNet")
    .plan__selected-badge(v-if="active")
      svg.plan__badge-icon(xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true")
        path(fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd")
  .plan__speed-card
    p.plan__speed-label {{ $t('wizard_contract.plans.prefix') }}
    .plan__speed-display
      span.plan__speed-num {{ velocidad_incluida }}
      span.plan__speed-unit Mbps
    p.plan__description(v-if="texto_tarifa_descripcion") {{ texto_tarifa_descripcion }}

  .plan__divider

  .plan__pricing
    .plan__price-main
      span.plan__price {{ numberFormatter(renta_mensual_con_iva) }}
      span.plan__currency MXN
    p.plan__period {{ $t('wizard_contract.plans.period') }}
    .plan__install-badge(v-if="!sin_costo_instalacion")
      | {{ $t('wizard_contract.plans.install') }}: {{ instalacion_con_iva }} MXN
    p.plan__install-badge(v-else)
      | {{ $t('wizard_contract.plans.install') }}: {{ $t('wizard_contract.plans.install_free') }}
</template>

<script>
import { numberFormatter } from "@/utils/formatters";

export default {
  props: {
    is_promotion: {
      type: Boolean,
      default: false,
    },
    active: {
      type: Boolean,
      default: false,
    },
    id: {
      type: Number,
      default: 0,
    },
    plazo_minimo_meses: {
      type: Number,
      default: 0,
    },
    nombre_comercial: {
      type: String,
      default: "",
    },
    velocidad_incluida: {
      type: Number,
      default: 0,
    },
    speed_promotion: {
      type: Number,
      default: 0,
    },
    instalacion_sin_iva: {
      type: Number,
      default: 0,
    },
    instalacion_con_iva: {
      type: Number,
      default: 0,
    },
    renta_mensual_con_iva: {
      type: Number,
      default: 0,
    },
    texto_tarifa_descripcion: {
      type: [String, Boolean],
      default: "",
    },
    plan_servicio_internet: {
      type: Array,
      default: () => [0, ""],
    },
    sin_costo_instalacion: {
      type: Boolean,
      default: false,
    },
  },
  setup() {
    return { numberFormatter };
  },
};
</script>

<style lang="scss" scoped>
.plan {
  @apply select-none relative overflow-hidden;
  @apply flex flex-col items-center text-center;
  @apply rounded-2xl p-6;
  @apply ring-2 ring-transparent;
  @apply transition-all duration-300 ease-in-out;
  @apply transform cursor-pointer;
  background: linear-gradient(160deg, #1a3d87 0%, #2a5aad 55%, #3b74d0 100%);
  box-shadow: 0 4px 24px rgba(26, 61, 135, 0.35);
  min-width: 180px;

  &.active {
    @apply scale-105;
    box-shadow: 0 8px 32px rgba(28, 222, 232, 0.4), 0 0 0 2px #1cdee8;
  }

  &:hover:not(.active) {
    transform: translateY(-3px) scale(1.01);
    box-shadow: 0 12px 36px rgba(26, 61, 135, 0.5);
  }

  &:active {
    @apply scale-110;
  }
}

// Decorative background circles
.plan__bg-deco {
  @apply absolute inset-0 pointer-events-none overflow-hidden;
}

.deco-circle {
  @apply absolute rounded-full;
  background: rgba(255, 255, 255, 0.06);
}

.deco-1 {
  width: 150px;
  height: 150px;
  top: -55px;
  right: -45px;
}

.deco-2 {
  width: 100px;
  height: 100px;
  bottom: -35px;
  left: -30px;
}

// Header: wifi icon + plan name
.plan__header {
  @apply relative flex flex-col items-center w-full mb-4 gap-1;
}

.plan__logo {
  width: 90px;
  object-fit: contain;
  margin-bottom: 10px;
  filter: brightness(0) invert(1);
  opacity: 0.9;
}

.plan__wifi-icon {
  width: 36px;
  height: 36px;
  color: white;
  margin-bottom: 4px;
  opacity: 0.85;
  display: block;
}

.plan__name {
  @apply text-white font-bold text-xl leading-tight;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
}

.plan__selected-badge {
  @apply mt-2 flex items-center justify-center;
  @apply w-7 h-7 rounded-full;
  @apply text-white;
  background-color: #1cdee8;
  box-shadow: 0 2px 8px rgba(28, 222, 232, 0.5);

  .plan__badge-icon {
    width: 16px;
    height: 16px;
    display: block;
  }
}

// White speed card
.plan__speed-card {
  @apply relative w-full bg-white rounded-xl px-4 py-4;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
}

.plan__speed-label {
  @apply text-bluegray-400 text-xs font-semibold uppercase tracking-widest mb-1;
}

.plan__speed-display {
  @apply flex items-baseline justify-center;
  gap: 4px;
}

.plan__speed-num {
  @apply font-extrabold leading-none;
  font-size: 3.75rem;
  color: #2a5aad;
}

.plan__speed-unit {
  @apply text-xl font-semibold text-bluegray-500;
  align-self: flex-end;
  margin-bottom: 4px;
}

.plan__description {
  @apply text-bluegray-400 text-xs font-light mt-2;
}

// Divider
.plan__divider {
  @apply w-full my-4;
  border-top: 1px solid rgba(255, 255, 255, 0.2);
}

// Pricing section
.plan__pricing {
  @apply relative flex flex-col items-center w-full text-white;
}

.plan__price-main {
  @apply flex flex-col items-center;
  gap: 2px;
}

.plan__price {
  @apply font-extrabold leading-none;
  font-size: 2.8rem;
  text-shadow: 0 1px 6px rgba(0, 0, 0, 0.2);
}

.plan__currency {
  @apply text-sm font-semibold uppercase tracking-widest;
  opacity: 0.75;
}

.plan__period {
  @apply text-sm mt-1;
  opacity: 0.75;
}

.plan__install-badge {
  @apply text-xs font-semibold mt-3 px-4 py-1 rounded-full;
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.2);
}
</style>
